import React, { forwardRef } from 'react';
import {
    array,
    bool,
    func,
    number,
    object,
    oneOf,
    oneOfType,
    string
} from 'prop-types';
import clsx from 'clsx';
import TextField from '@material-ui/core/TextField';

import InputAdornment from './InputAdornment';

import defaultClasses from './Autocomplete.module.css';

const AutocompleteInput = forwardRef(({
    adornmentPosition,
    AdornmentProps,
    InputProps,
    classes,
    overrideClass,
    overrideErrorClass,
    overrideLabelClass,
    disabled,
    itemToString,
    multiple,
    onClick,
    onDelete,
    selectedItem,
    ...props
}, ref) => {
    let adornment = null;
    let endAdornment = null;
    let startAdornment = null;
    const isEndAdornment = adornmentPosition === 'end';
    const isStartAdornment = adornmentPosition === 'start';
    if (multiple) {
        adornment = selectedItem.map((item, index) => (
            <InputAdornment
                disabled={disabled}
                key={itemToString(item)}
                item={item}
                label={itemToString(item)}
                onClick={onDelete}
                onDelete={onDelete}
                tabIndex={-1}
                {...AdornmentProps}
                classes={{
                    ...AdornmentProps.classes,
                    root: clsx(
                        {
                            [defaultClasses.endAdornment]:   isEndAdornment,
                            [defaultClasses.startAdornment]: isStartAdornment
                        },
                        AdornmentProps.classes.root
                    )
                }}
            />
        ));
    }
    if (isStartAdornment) {
        startAdornment = adornment;
    } else if (isEndAdornment) {
        endAdornment = adornment;
    }

    function handleClick(event) {
        if (disabled) {
            return;
        }
        onClick(event);
    }

    return (
        <React.Fragment>
            <TextField
                disabled={disabled}
                FormHelperTextProps={{
                    classes: {
                        root: clsx(
                            defaultClasses.helperText,
                            classes.helperText
                        ),
                        error: clsx(defaultClasses.helperTextError, overrideErrorClass)
                    }
                }}
                fullWidth
                InputLabelProps={{
                    classes: {
                        error: clsx(
                            defaultClasses.labelError,
                            classes.labelError,
                            overrideErrorClass
                        ),
                        root: clsx(defaultClasses.label, classes.label, overrideLabelClass)
                    }
                }}
                InputProps={{
                    inputRef: ref,
                    classes:  {
                        root: clsx(
                            defaultClasses.inputRoot,
                            classes.inputRoot
                        ),
                        input: clsx(defaultClasses.input, classes.input, overrideClass),
                        error: clsx(defaultClasses.cssErrorUnderline, overrideErrorClass),
                    },
                    startAdornment,
                    ...InputProps
                }}
                onClick={handleClick}
                {...props}
            />
            {endAdornment}
        </React.Fragment>
    );
});

AutocompleteInput.displayName = 'AutocompleteInput';

AutocompleteInput.defaultProps = {
    AdornmentProps: {
        classes: {}
    },
    itemToString: func,
    onDelete:     func
};

AutocompleteInput.propTypes = {
    adornmentPosition: oneOf(['start', 'end']),
    AdornmentProps:    object,
    InputProps:        object,
    classes:           object,
    itemToString:      func,
    multiple:          bool,
    onDelete:          func,
    selectedItem:      oneOfType([array, object, number, string])
};

export default AutocompleteInput;
